import React, { Component, Fragment } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { CreateModal as ProductGroupCreateModal } from '~/ProductGroup';
import ProductGroupDeleteModal from '~/ProductGroupDeleteModal';
import ListItem from '~/ProductGroupList/ListItem/ItemContainer';
import ProductGroupNotificationSettingsModal from '~/ProductGroupNotificationSettingsModal';
import ProductGroupRenameModal from '~/ProductGroupRenameModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import SimplePaginator from '~/UI/SimplePaginator/SimplePaginator';

import styles from './styles.less';

interface Props {
	groups: UserProductGroup[] | null;
	isFetching: boolean;
	showModule: boolean | null;
	hasNextPage: boolean;
	actions?: (group: UserProductGroup, index: number) => React.ReactNode;
	itemFooter?: (group: UserProductGroup, index: number) => React.ReactNode;
	onFetch(): void;
	onChange?(): void;
}

type State =
	| {
			popover: null;
			popoverGroup: null;
	  }
	| {
			popover: string;
			popoverGroup: UserProductGroup;
	  };

export default class GroupList extends Component<Props, State> {
	static defaultProps = {
		groups: null,
		showModule: false,
	};

	state: State = {
		popover: null,
		popoverGroup: null,
	};

	mounted = true;

	componentDidMount() {
		this.props.onFetch();
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	render() {
		const {
			props: { groups, isFetching, hasNextPage, showModule, actions, itemFooter, onFetch, onChange },
		} = this;

		const { state } = this;

		if (!groups) {
			return null;
		}

		if (!isFetching && groups.length === 0) {
			return <ProductInstructions />;
		}

		return (
			<Fragment>
				<div className={styles.in}>
					<ul className={styles.groupList}>
						{groups.map((group, index) => (
							<li key={group.id}>
								<ListItem
									actions={actions?.(group, index)}
									error={itemFooter?.(group, index)}
									productGroup={group}
									showDownloadPopover={state.popover === 'download' && group.id === state.popoverGroup.id}
									showModule={Boolean(showModule)}
									showOptionsPopover={state.popover === 'options' && group.id === state.popoverGroup.id}
									onCloseDownloadPopover={this.closePopover}
									onCloseOptionsPopover={this.closePopover}
									onCopy={() => this.setState({ popover: 'copy', popoverGroup: group })}
									onDelete={() => this.setState({ popover: 'delete', popoverGroup: group })}
									onDownloadClick={() => this.setState({ popover: 'download', popoverGroup: group })}
									onNotificationsClick={() =>
										this.setState({
											popover: 'notificationSettings',
											popoverGroup: group,
										})
									}
									onOptionsClick={() => this.setState({ popover: 'options', popoverGroup: group })}
									onRename={() => this.setState({ popover: 'rename', popoverGroup: group })}
								/>
							</li>
						))}
					</ul>
				</div>

				{isFetching ? <ActivityIndicator /> : null}
				{hasNextPage && !isFetching ? <SimplePaginator onLoadMore={onFetch} /> : null}

				{state.popover === 'rename' ? (
					<ProductGroupRenameModal group={state.popoverGroup} onClose={this.closePopover} onSave={this.closePopover} />
				) : null}

				{state.popover === 'delete' ? (
					<ProductGroupDeleteModal
						group={state.popoverGroup}
						onClose={this.closePopover}
						onConfirm={() => {
							this.closePopover();
							onChange?.();
						}}
					/>
				) : null}

				{state.popover === 'notificationSettings' ? (
					<ProductGroupNotificationSettingsModal
						group={state.popoverGroup}
						onClose={this.closePopover}
						onChange={this.closePopover}
					/>
				) : null}

				{state.popover === 'copy' ? (
					<ProductGroupCreateModal
						groupType={state.popoverGroup.type}
						templateGroup={state.popoverGroup}
						onClose={this.closePopover}
						onSave={this.closePopover}
					/>
				) : null}
			</Fragment>
		);
	}

	closePopover = (): any => {
		if (this.mounted) {
			this.setState({ popover: null, popoverGroup: null });
		}
	};
}
